import { useMemo, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '../../../constants/routes'
import MuiStack from '@mui/material/Stack'
import MuiIconButton from '@mui/material/IconButton'
import { DataGridPro as DataGrid, GridToolbar } from '@mui/x-data-grid-pro'
import { useGetClientsQuery } from '../slice'
import { useGetFieldsQuery } from '../../../services/fields/slice'
import { useGetUsersQuery } from '../../users/slice'
import { LicenseInfo } from '@mui/x-license-pro'
import AppConfig from '../../../config'
import EditNoteIcon from '@mui/icons-material/EditNote'
import DeleteIcon from '@mui/icons-material/Delete'
import DeleteClient from './deleteClient'

LicenseInfo.setLicenseKey(AppConfig.mui.licenseKey)

const ClientList = () => {
  const { data: clients, isLoading: isClientsLoading } = useGetClientsQuery()
  const { data: fields, isLoading: isFieldsLoading } = useGetFieldsQuery('clients')
  const { data: users, isLoading: isUsersLoading } = useGetUsersQuery()

  const [rows, setRows] = useState([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [currentClient, setCurrentClient] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (clients) {
      setRows(clients)
    }
  }, [clients])

  const handleDeleteClick = (client) => {
    setCurrentClient(client)
    setDeleteDialogOpen(true)
  }

  const columns = useMemo(() => {
    const handleEditClick = (client) => {
      navigate(`${ROUTES.CLIENTS}/${client.id}`)
    }

    if (clients && fields && users) {
      const clientColumns = fields
        .map((field) => {
          const type = field.type === 'boolean' ? 'boolean' : field.type === 'date' ? 'date' : undefined
          return {
            field: field.name,
            headerName: field.label,
            description: field.description,
            width: field.width || 200,
            editable: false,
            type,
            priority: field.priority || 999999,
            valueGetter: (params) => {
              if (params.field.type === 'date') return new Date(params.value)
              if (field.type === 'reference' && field.referenceEntity === 'user') {
                const user = users.find((user) => user.id === params.value)
                if (!user) return params.value
                return `${user.givenName} ${user.familyName}`
              }
              return params.value
            },
          }
        })
        .sort((a, b) => a.priority - b.priority)
      return [
        ...clientColumns,
        {
          field: 'actions',
          headerName: 'Action',
          flex: 1,
          sortable: false,
          // disableClickEventBubbling: true,

          renderCell: (params) => {
            const onEditClick = (e) => {
              const currentRow = params.row
              handleEditClick(currentRow)
            }
            const onDeleteClick = (e) => {
              const currentRow = params.row
              handleDeleteClick(currentRow)
            }

            return (
              <MuiStack direction="row">
                <MuiIconButton size="small" onClick={onEditClick}>
                  <EditNoteIcon />
                </MuiIconButton>
                <MuiIconButton size="small" onClick={onDeleteClick}>
                  <DeleteIcon />
                </MuiIconButton>
              </MuiStack>
            )
          },
        },
      ]
    }
    return []
  }, [clients, fields, users, navigate])

  return (
    <>
      <DataGrid
        rows={rows}
        columns={columns}
        density="compact"
        loading={isClientsLoading || isFieldsLoading || isUsersLoading}
        pagination
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
        }}
        slots={{
          toolbar: GridToolbar,
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus': {
            outline: 'none !important',
          },
        }}
      />
      <DeleteClient client={currentClient} open={deleteDialogOpen} setOpen={setDeleteDialogOpen} />
    </>
  )
}

export default ClientList
